import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import GlobalStyles from './styles/shared/GlobalStyles';
import AppRoutes from './routes';
import AgentMarkdownPage from './components/ui/AgentMarkdownPage';
import { isAgentMarkdownPath } from './utils/agent/agentMarkdown';
import theme from './Theme';
import ChatProvider from './chat/ChatProvider';
import CookieConsentBanner from './components/cookies/CookieConsentBanner';
import GoogleOneTap from './components/auth/GoogleOneTap';
import store from './redux/store';
import { logout } from './redux/features/authSlice';
import { getApiUrl } from './redux/api';
import {
  initActivityTracking,
  startPeriodicValidation,
} from './utils/auth/tokenManager';

// FloatingChat (MUI widgets, styled-components, content moderation) is only
// needed for authenticated users, so load it lazily to keep it out of the
// initial bundle for logged-out/onboarding pages.
const FloatingChat = lazy(() => import('./chat/FloatingChat'));

// Helper function to safely get token from localStorage
const getTokenFromStorage = () => {
  try {
    const storedToken = localStorage.getItem('token');
    // Basic validation: token should be a string and have JWT format
    if (storedToken && typeof storedToken === 'string' && storedToken.includes('.')) {
      const parts = storedToken.split('.');
      if (parts.length === 3) {
        return storedToken;
      }
    }
    // If token is invalid, remove it
    if (storedToken) {
      localStorage.removeItem('token');
    }
    return null;
  } catch (error) {
    console.error('Error reading token from localStorage:', error);
    return null;
  }
};

function AppContent() {
  const location = useLocation();
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getTokenFromStorage());
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(token));

  const isOnboardingPage = location.pathname.includes('/onboarding')
                          || location.pathname.includes('/brand-onboarding')
                          || location.pathname.includes('/influencer-onboarding');

  // Listen for token changes in localStorage
  useEffect(() => {
    const checkToken = () => {
      const newToken = getTokenFromStorage();
      if (newToken !== token) {
        setToken(newToken);
      }
    };

    checkToken();

    // Listen for storage events from other tabs
    const handleStorage = (e) => {
      if (e.key === 'token') {
        checkToken();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Listen for custom login event
    const handleLogin = () => {
      setTimeout(() => {
        checkToken();
      }, 50);
    };
    window.addEventListener('userLoggedIn', handleLogin);

    const handleLogout = () => {
      setToken(null);
    };
    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const stopTracking = initActivityTracking();
    const stopValidation = startPeriodicValidation({
      apiUrl: getApiUrl(),
      onExpired: () => {
        store.dispatch(logout());
      },
      onSessionTimeout: () => {
        store.dispatch(logout());
      },
    });

    return () => {
      stopTracking();
      stopValidation();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (token) {
      try {
        if (typeof token !== 'string' || !token.includes('.')) {
          throw new Error('Token is not a valid JWT format');
        }

        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Token does not have 3 parts');
        }

        // Decode the payload (second part)
        const payload = JSON.parse(atob(tokenParts[1]));

        if (payload && payload.user_type) {
          setUserType(payload.user_type);
          setIsAuthenticated(true);
        } else {
          throw new Error('Token payload missing user_type');
        }
      } catch (error) {
        console.error('Invalid token format:', error);
        localStorage.removeItem('token');
        setToken(null);
        setUserType(null);
        setIsAuthenticated(false);
      }
    } else {
      setUserType(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, [token]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <ChatProvider userType={userType}>
      <AppRoutes isAuthenticated={isAuthenticated} userType={userType} />
      {isAuthenticated && !isOnboardingPage && userType !== 'admin' && (
        <Suspense fallback={null}>
          <FloatingChat />
        </Suspense>
      )}
      {isAuthenticated ? null : <GoogleOneTap />}
      {userType === 'admin' ? null : <CookieConsentBanner />}
    </ChatProvider>
  );
}

function AppShell() {
  const location = useLocation();
  if (isAgentMarkdownPath(location.pathname)) {
    return <AgentMarkdownPage pathname={location.pathname} />;
  }
  return <AppContent />;
}

function App() {
  return (
    <MUIThemeProvider theme={theme}>
      <StyledThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppShell />
        </Router>
      </StyledThemeProvider>
    </MUIThemeProvider>
  );
}

export default App;
